import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSubscriptionContext } from "../contexts/SubscriptionContext";
import { LoadingSpinner } from "./LoadingSpinner";
import { PlanDetailsTooltip } from "./PlanDetailsTooltip";
import { BottomSheet } from "./BottomSheet";
import { KairosNameExplanation } from "./KairosNameExplanation";
import { HourglassSpinner } from "@cognixis-technology/common-react-components";
import { isValidSubscription } from "../utils/subscriptionUtils";
import type { ServicePlan } from "../../../shared/types/servicePlans";
import { PoweredByCognixis } from "./PoweredByCognixis";
import { UserProfileDropdown } from "./UserProfileDropdown";
import kairosLogoWithName from "../images/kairos_logo_with_name.png";
import explorerImage from "../images/Kairos Explorer.png";
import navigatorImage from "../images/Kairos Navigator.png";
import acceleratorImage from "../images/Kairos Accelerator.png";
import masterImage from "../images/Kairos Master.png";
import dashboardIcon from "../images/DashboardIcon.png";
import jobSearchIcon from "../images/JobsearchIcon.png";
import agentsIcon from "../images/AgentsIcon.png";
import "./Sidebar.css";

interface SidebarProps {
  /** Loading states for each menu section */
  loadingStates?: {
    dashboard?: boolean;
    jobSearch?: boolean;
    profile?: boolean;
    settings?: boolean;
    admin?: boolean;
  };
  activeSection?: string;
  /** Mobile menu state - only affects mobile behavior */
  isMobileMenuOpen?: boolean;
  /** Callback to close mobile menu */
  onCloseMobileMenu?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  loadingStates = {},
  activeSection,
  isMobileMenuOpen = false,
  onCloseMobileMenu,
}) => {
  const { profile } = useAuth();
  const {
    subscription,
    plans,
    isLoadingSubscription,
    isLoadingPlans,
    getPlanById,
    reloadSubscription,
  } = useSubscriptionContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPlan, setCurrentPlan] = useState<ServicePlan | null>(null);
  const [showKairosInfo, setShowKairosInfo] = useState(false);
  const [showPlanDetails, setShowPlanDetails] = useState(false);

  // Check if we're on the service plans page
  const isServicePlansPage = location.pathname === "/service-plans";

  // Update current plan when subscription or plans change
  useEffect(() => {
    if (!subscription?.currentPlanId) {
      setCurrentPlan(null);
      return;
    }

    if (isLoadingPlans) {
      return; // Wait for plans to load
    }

    const plan = getPlanById(subscription.currentPlanId);
    if (plan) {
      setCurrentPlan(plan);
    } else {
      setCurrentPlan(null);
    }
  }, [subscription?.currentPlanId, plans, isLoadingPlans, getPlanById]);

  const handlePlanClick = () => {
    // Close popup when navigating to service plans page
    setShowPlanDetails(false);
    navigate("/service-plans");
  };

  const handlePlanCardClick = (e: React.MouseEvent) => {
    // If on service plans page, navigate; otherwise show details
    if (isServicePlansPage) {
      handlePlanClick();
    } else {
      e.stopPropagation();
      setShowPlanDetails(true);
    }
  };

  // Close popup when service plans page becomes visible
  useEffect(() => {
    if (isServicePlansPage) {
      setShowPlanDetails(false);
    }
  }, [isServicePlansPage]);

  // Get plan-specific color class based on plan ID
  const getPlanColorClass = (planId: string | undefined): string => {
    if (!planId) return "plan-color-default";
    const planNum = parseInt(planId);
    if (planNum >= 1 && planNum <= 5) {
      return `plan-color-${planId}`;
    }
    return "plan-color-default";
  };

  // Get plan image based on plan ID
  const getPlanImage = (planId: string | undefined): string | null => {
    if (!planId) return null;
    const planNum = parseInt(planId);
    switch (planNum) {
      case 1:
        return explorerImage;
      case 2:
        return navigatorImage;
      case 3:
        return acceleratorImage;
      case 4:
        return masterImage;
      default:
        return null;
    }
  };

  const renderNavItemContent = (
    isLoading: boolean,
    badge?: string | number,
  ) => {
    if (isLoading) {
      return (
        <div className="nav-loading">
          <LoadingSpinner size="small" />
        </div>
      );
    }
    if (badge) {
      return <span className="nav-badge">{badge}</span>;
    }
    return null;
  };

  // Helper component for navigation items with loading states
  const NavItem: React.FC<{
    to: string;
    icon: string; // Emoji fallback
    iconImage?: string; // Optional image src for icon (takes precedence over emoji)
    text: string;
    badge?: string | number;
    section: string;
    onClick?: () => void;
  }> = ({ to, icon, iconImage, text, badge, section, onClick }) => {
    const isLoading =
      loadingStates[section as keyof typeof loadingStates] || false;
    const isActive = activeSection === section;

    return (
      <NavLink
        to={to}
        end
        onClick={onClick}
        className={({ isActive: routerActive }: { isActive: boolean }) =>
          `sidebar-nav-item ${isActive || routerActive ? "active" : ""} ${isLoading ? "loading" : ""}`
        }
        data-tour-id={`nav-${section}`}
      >
        <span className="sidebar-nav-icon">
          {iconImage ? (
            <img
              src={iconImage}
              alt={text}
              className="sidebar-nav-icon-image"
            />
          ) : (
            icon
          )}
        </span>
        <span className="nav-text">{text}</span>
        {renderNavItemContent(isLoading, badge)}
      </NavLink>
    );
  };

  // Close mobile menu when navigation item is clicked
  const handleNavClick = () => {
    if (onCloseMobileMenu) {
      onCloseMobileMenu();
    }
  };

  return (
    <div
      className={`sidebar ${isMobileMenuOpen ? "mobile-open" : ""}`}
      data-tour-id="sidebar"
    >
      {/* Sidebar Header */}
      <div className="sidebar-header">
        <div className="sidebar-brand">
          <img
            src={kairosLogoWithName}
            alt="Kairos Pro"
            className="sidebar-brand-logo"
          />
          <button
            onClick={() => setShowKairosInfo(true)}
            className="sidebar-info-button"
            aria-label="What is Kairos?"
            title="What is Kairos?"
            type="button"
          >
            ℹ️
          </button>
          <BottomSheet
            isOpen={showKairosInfo}
            onClose={() => setShowKairosInfo(false)}
            title="What is Kairos?"
          >
            <KairosNameExplanation />
          </BottomSheet>
        </div>
        <div className="sidebar-tagline">Job Search Intelligence</div>
        {/* Mobile close button - only visible on mobile */}
        {isMobileMenuOpen && onCloseMobileMenu && (
          <button
            className="sidebar-mobile-close"
            onClick={onCloseMobileMenu}
            aria-label="Close menu"
            type="button"
          >
            ✕
          </button>
        )}
      </div>

      {/* User Profile Section */}
      <div className="sidebar-user" data-tour-id="user-profile">
        <NavLink
          to="/profile"
          className="sidebar-user-avatar"
          title="View Profile"
        >
          {profile?.firstName?.charAt(0)}
          {profile?.lastName?.charAt(0)}
        </NavLink>
        <div className="sidebar-user-info">
          <div className="sidebar-user-name">
            {profile?.firstName} {profile?.lastName}
          </div>
          <div className="sidebar-user-email" title={profile?.email}>
            {profile?.email}
          </div>
        </div>
        <UserProfileDropdown />
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        <div className="nav-section">
          <NavItem
            to="/dashboard"
            icon="📊"
            iconImage={dashboardIcon}
            text="Dashboard"
            section="dashboard"
            onClick={handleNavClick}
          />
          <NavItem
            to="/job-search"
            icon="🔍"
            iconImage={jobSearchIcon}
            text="Job Search"
            section="jobSearch"
            onClick={handleNavClick}
          />
          <NavItem
            to="/agents"
            icon="🤖"
            iconImage={agentsIcon}
            text="Agents"
            section="agents"
            onClick={handleNavClick}
          />
        </div>
      </nav>

      {/* Sidebar Footer */}
      <div className="sidebar-footer">
        {/* Token Balance Display - Show for all authenticated users with subscription */}
        {subscription && subscription.tokensRemaining !== undefined && (
          <div
            className={`sidebar-token-balance clickable ${isLoadingSubscription ? "loading" : ""}`}
            onClick={() =>
              !isLoadingSubscription && reloadSubscription(false, true)
            }
            title={
              isLoadingSubscription
                ? "Refreshing token balance..."
                : "Click to refresh token balance"
            }
          >
            <div className="token-balance-label">TOKENS</div>
            {isLoadingSubscription ? (
              <div className="token-balance-loading">
                <div className="token-balance-spinner">
                  <HourglassSpinner fullPage={false} />
                </div>
                <div className="token-balance-loading-message">
                  Refreshing token balance...
                </div>
              </div>
            ) : (
              <>
                <div className="token-balance-value">
                  {subscription.tokensRemaining.toLocaleString()} /{" "}
                  {subscription.tokenAllowance?.toLocaleString() || "—"}
                </div>
                {subscription.tokensRemaining <
                  (subscription.tokenAllowance || 0) * 0.1 && (
                  <div className="token-balance-warning">
                    Low tokens remaining
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Current Service Plan Display - Only show if user has valid Stripe subscription */}
        {!isLoadingSubscription &&
          isValidSubscription(subscription) &&
          subscription && (
            <>
              <div
                className={`sidebar-plan-display ${getPlanColorClass(subscription.currentPlanId)}`}
                onClick={handlePlanCardClick}
                data-tour-id="plan-card"
              >
                <div className="plan-display-content">
                  {isLoadingPlans ? (
                    <div className="plan-display-loading">
                      <LoadingSpinner size="small" />
                    </div>
                  ) : (
                    <>
                      <div className="plan-display-label">Current Plan</div>
                      {getPlanImage(subscription.currentPlanId) ? (
                        <div className="plan-display-image-wrapper">
                          <img
                            src={getPlanImage(subscription.currentPlanId)!}
                            alt={currentPlan?.serviceName || "Plan"}
                            className="plan-display-image"
                          />
                        </div>
                      ) : (
                        <div className="plan-display-name">
                          {currentPlan?.serviceName
                            ? currentPlan.serviceName
                            : subscription.currentPlanId
                              ? `Plan ${subscription.currentPlanId}`
                              : "No Plan"}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="plan-display-gradient"></div>
              </div>
              <BottomSheet
                isOpen={showPlanDetails && !isServicePlansPage}
                onClose={() => setShowPlanDetails(false)}
                title="Plan Details"
              >
                <PlanDetailsTooltip
                  plan={currentPlan}
                  subscription={subscription}
                />
              </BottomSheet>
            </>
          )}

        {/* Powered by Cognixis - Bottom of Sidebar */}
        <div className="sidebar-powered-by">
          <PoweredByCognixis />
        </div>
      </div>
    </div>
  );
};
